import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Send } from "lucide-react";
import { Button } from "@shared/ui/button";
import { cn } from "@shared/lib/utils";
import { DirIcon } from "./DirIcon";

export interface RegisteredComposerAction {
  label: string;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
}

interface RegisteredActionRecord extends RegisteredComposerAction {
  id: string;
}

interface ComposerActionContextValue {
  action: RegisteredActionRecord | null;
  register: (action: RegisteredComposerAction) => string;
  update: (id: string, action: RegisteredComposerAction) => void;
  unregister: (id: string) => void;
}

const ComposerActionContext = createContext<ComposerActionContextValue | null>(null);

export function ComposerActionProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<RegisteredActionRecord[]>([]);

  const register = useCallback((action: RegisteredComposerAction) => {
    const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    const record: RegisteredActionRecord = { ...action, id };
    setActions((current) => [...current, record]);
    return id;
  }, []);

  const unregister = useCallback((id: string) => {
    setActions((current) => current.filter((item) => item.id !== id));
  }, []);

  const update = useCallback((id: string, action: RegisteredComposerAction) => {
    setActions((current) =>
      current.map((item) => {
        if (item.id !== id) return item;
        if (
          item.label === action.label &&
          item.onClick === action.onClick &&
          item.visible === action.visible &&
          item.disabled === action.disabled
        ) {
          return item;
        }
        return { ...item, ...action };
      }),
    );
  }, []);

  const visibleAction =
    [...actions].reverse().find((item) => item.visible !== false) ?? null;

  const value = useMemo(
    () => ({ action: visibleAction, register, update, unregister }),
    [register, unregister, update, visibleAction],
  );

  return (
    <ComposerActionContext.Provider value={value}>
      {children}
    </ComposerActionContext.Provider>
  );
}

export function useRegisterComposerAction(action: RegisteredComposerAction) {
  const context = useContext(ComposerActionContext);
  const idRef = useRef<string | null>(null);
  const actionRef = useRef(action);
  const onClickRef = useRef<() => void>(() => actionRef.current.onClick());
  actionRef.current = action;
  const register = context?.register;
  const unregister = context?.unregister;
  const update = context?.update;

  useEffect(() => {
    if (!register || !unregister) return undefined;
    const id = register({ ...actionRef.current, onClick: onClickRef.current });
    idRef.current = id;
    return () => {
      unregister(id);
      idRef.current = null;
    };
  }, [register, unregister]);

  useEffect(() => {
    if (!update || !idRef.current) return;
    update(idRef.current, { ...action, onClick: onClickRef.current });
  }, [action.disabled, action.label, action.visible, update]);
}

export function useComposerAction() {
  return useContext(ComposerActionContext)?.action ?? null;
}

export function ComposerFab({ className }: { className?: string }) {
  const action = useComposerAction();

  if (!action) return null;

  return (
    <Button
      type="button"
      className={cn(
        "fixed bottom-[calc(env(safe-area-inset-bottom)+1rem)] end-4 z-40 h-[52px] rounded-full px-4 shadow-lg sm:hidden",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
      disabled={action.disabled}
      onClick={action.onClick}
      data-testid="composer-fab"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <DirIcon icon={Send} className="me-2 h-4 w-4" aria-hidden="true" />
      {action.label}
    </Button>
  );
}
